import useSWRInfinite from 'swr/infinite';
import { MyImagesResponse, ImageGenerationRecord } from '../types/MyImages';
import { SaagasImage } from '../types/SaagasImage';
import imagesClient from '../utils/apiClients/imagesClient';

export const fetcher = (url: string) => imagesClient.get(url).then(res => res.data);

const API_ENDPOINT = '/media';
const PAGE_SIZE = 10;

const getKey = (pageIndex: number, previousPageData: MyImagesResponse | null, includePersona: boolean = false) => {
  // If reached the end, return null to stop fetching
  if (previousPageData && previousPageData.media.length === 0) return null;
  
  // Check if we've loaded all pages based on paging info
  if (previousPageData && previousPageData.paging) {
    const { offset, size, total } = previousPageData.paging;
    if (offset + size >= total) return null;
  }

  // Construct the API endpoint with pagination parameters
  const categories = includePersona 
    ? 'categories=vanilla&categories=art_style&categories=persona'
    : 'categories=vanilla&categories=art_style';
  return `${API_ENDPOINT}?offset=${pageIndex * PAGE_SIZE}&size=${PAGE_SIZE}&bookmarkedOnly=false&${categories}`;  
};

const useFetchImages = (includePersona: boolean = false) => {
  const getKeyWithPersona = (pageIndex: number, previousPageData: MyImagesResponse | null) => 
    getKey(pageIndex, previousPageData, includePersona);

  const {
    data,
    error,
    size,
    setSize,
    mutate
  } = useSWRInfinite<MyImagesResponse>(getKeyWithPersona, fetcher, {
    revalidateOnFocus: true,
    dedupingInterval: 60000 
  });

  // Convert ImageGenerationRecord to SaagasImage
  const convertToSaagasImage = (record: ImageGenerationRecord): SaagasImage & { thumbnailUrl?: string | null; type?: 'image' | 'video'; videoCategories?: string[] | null } => {
    return {
      id: record.id,
      prompt: record.prompt,
      url: record.url,
      liked: record.liked,
      createdAt: record.createdAt,
      bookmarked: record.bookmarked,
      artStyleId: record.artStyleId || undefined,
      width: record.width ? String(record.width) : undefined,
      height: record.height ? String(record.height) : undefined,
      imageCategories: record.imageCategories || undefined,
      edited: record.edited,
      status: record.status,
      projectId: record.projectId || undefined,
      modelName: record.modelName || undefined,
      // Include video-related fields for video detection and thumbnail display
      thumbnailUrl: record.thumbnailUrl || undefined,
      type: record.type,
      videoCategories: record.videoCategories || undefined,
    };
  };

  const images = data ? data.flatMap(page => page.media.map(convertToSaagasImage)) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isError = error;

  // Determine if there are more pages to load based on paging info
  const hasMore = data && data.length > 0 
    ? (() => {
      const lastPage = data[data.length - 1];
      if (lastPage.paging) {
        const { offset, size, total } = lastPage.paging;
        return offset + size < total;
      }
      return lastPage.media.length === PAGE_SIZE;
    })()
    : true;

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setSize(size + 1);
    }
  };

  return {
    images,
    isLoadingInitialData,
    isLoadingMore,
    isError,
    loadMore,
    hasMore,
    mutate
  };
};

export default useFetchImages;
